import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import api from '../services/api';
import NotificationSnackbar from './NotificationSnackbar';

function BoothAssignModal({ open, onClose, booth, onAssigned }) {
  const [bookers, setBookers] = useState([]);
  const [selectedBooker, setSelectedBooker] = useState('');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    if (open) {
      setSelectedBooker(''); // Reset selection on open
      setLoading(true);
      api.get('/api/bookings/active-bookers')
        .then(res => {
          // Only show bookers of type exhibitor or sponsor, and status verified and paid
          const eligible = (res.data || []).filter(b => {
            const cat = b.packageId?.category?.toLowerCase();
            const isExhibitorOrSponsor = cat === 'exhibitor' || cat === 'sponsor';
            const isVerified = b.status === 'verified' || b.status === 'approved';
            const isPaid = b.paymentStatus === 'paid';
            return isExhibitorOrSponsor && isVerified && isPaid;
          });
          setBookers(eligible);
          // If only one eligible, auto-select
          if (eligible.length === 1) setSelectedBooker(eligible[0]._id);
        })
        .catch(err => setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to fetch bookers', severity: 'error' }))
        .finally(() => setLoading(false));
    } else {
      setBookers([]);
      setSelectedBooker('');
    }
  }, [open]);

  const handleAssign = async () => {
    if (!selectedBooker) {
      setSnackbar({ open: true, message: 'Select a booker', severity: 'error' });
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/api/booths/assign', { boothId: booth._id, bookingId: selectedBooker });
      const { booth: boothResult, ticket } = res.data;
      setSnackbar({ open: true, message: `Booth assigned and ticket verified! Booth: ${boothResult.name}, Ticket ID: ${ticket._id}`, severity: 'success' });
      onAssigned();
      onClose();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Assign failed', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Assign Booth</DialogTitle>
      <DialogContent>
        <Typography mb={2}>Assign booth <b>{booth?.name}</b> to an eligible Exhibitor or Sponsor (Verified & Paid):</Typography>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="booker-select-label">Booker</InputLabel>
          <Select
            labelId="booker-select-label"
            value={selectedBooker}
            label="Booker"
            onChange={e => setSelectedBooker(e.target.value)}
            disabled={loading || bookers.length === 0}
            displayEmpty
          >
            <MenuItem value="" disabled>
              {loading ? 'Loading...' : bookers.length === 0 ? 'No eligible bookers found' : 'Select Booker'}
            </MenuItem>
            {bookers.map(b => (
              <MenuItem key={b._id} value={b._id}>
                {b.userId?.name || b.bookingDetails?.bookerName || b.bookingDetails?.companyDetails?.name || b.userId?.email || b._id}
                {' '}({b.packageId?.category})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <NotificationSnackbar
          open={snackbar.open}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          message={snackbar.message}
          severity={snackbar.severity}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleAssign} variant="contained" disabled={loading}>Assign</Button>
      </DialogActions>
    </Dialog>
  );
}

export default BoothAssignModal;
